import { motion } from "framer-motion";
import { Blocks, ServerCog, Code2, ArrowRight, Check } from "lucide-react";
import { PageHeader, DISCORD_URL, SEO, fadeUp, cardReveal, staggerContainer } from "@/components/shared";

const SERVICES = [
  {
    icon: Blocks,
    title: "Minecraft Plugins",
    description:
      "Custom plugins and server features built for performance, stability, and your exact needs.",
    points: ["Custom gameplay mechanics", "Performance-focused code", "Updates and maintenance"],
    testid: "service-detail-minecraft-plugins",
  },
  {
    icon: ServerCog,
    title: "Server Solutions",
    description:
      "Configuration, optimization, and setup assistance to keep your server running at its best.",
    points: ["Full server setup assistance", "Performance tuning", "Ongoing configuration help"],
    testid: "service-detail-server-solutions",
  },
  {
    icon: Code2,
    title: "Development Services",
    description:
      "Tailored development solutions for Minecraft communities, from tools to integrations.",
    points: ["Custom tools and systems", "Integrations with your setup", "Solutions built around you"],
    testid: "service-detail-development-services",
  },
];

export default function ServicesPage() {
  return (
    <>
      <SEO
        title="Services — Vexora Development"
        description="Minecraft plugins, server solutions, and tailored development services for Minecraft communities. Get performance-focused development from Vexora Development."
      />
      <PageHeader
        eyebrow="Our Services"
        title={
          <>
            What we{" "}
            <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-purple-500 bg-clip-text text-transparent">
              build
            </span>{" "}
            for you
          </>
        }
        subtitle="Focused expertise across every layer of your Minecraft server — from the first plugin to a fully optimized network."
      />

      <section data-testid="services-detail-section" className="relative pb-24 lg:pb-28">
        <div
          className="absolute top-0 right-0 w-[600px] h-[400px] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(147,51,234,0.12) 0%, rgba(9,10,15,0) 70%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {SERVICES.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.21, 0.65, 0.35, 1] }}
              data-testid={service.testid}
              className="group relative grid grid-cols-1 lg:grid-cols-12 gap-8 rounded-3xl border border-purple-500/15 bg-[#11131f]/80 p-8 sm:p-10 transition-all duration-300 hover:border-purple-500/50 hover:shadow-[0_16px_50px_rgba(147,51,234,0.18)]"
            >
              <div className="lg:col-span-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-purple-500/25 bg-purple-500/10 transition-colors duration-300 group-hover:bg-purple-500/20">
                  <service.icon className="w-6 h-6 text-purple-400" />
                </span>
                <h2 className="mt-6 text-2xl sm:text-3xl font-bold tracking-tight text-white">
                  {service.title}
                </h2>
                <p className="mt-4 text-base text-slate-300 leading-relaxed max-w-xl">
                  {service.description}
                </p>
                <a
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`service-discord-${i}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Request this service
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
              <ul className="lg:col-span-5 space-y-3 self-center">
                {service.points.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-3 rounded-xl border border-purple-500/10 bg-purple-500/5 px-4 py-3"
                  >
                    <Check className="w-4 h-4 mt-0.5 text-purple-400 shrink-0" />
                    <span className="text-sm text-slate-300">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
}
